<script setup lang="ts">
const translations = useTranslationStore();
const { t } = useI18n();

const props = defineProps<{
  numPerPage: number;
  page: number;
  count: number;
  placeholder?: boolean;
  disableNumPerPageSelect?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:numPerPage", numPerPage: number): void;
  (e: "update:page", page: number): void;
}>();

const numPages = computed(() => Math.ceil(props.count / props.numPerPage));
const _numPerPage = computed<number>({
  get: () => {
    return props.numPerPage;
  },
  set: (page: string | number) => {
    if (typeof page === "string") {
      emit("update:numPerPage", parseInt(page));
    } else {
      emit("update:numPerPage", page);
    }
  },
});

const _page = computed<number>({
  get: () => {
    return props.page;
  },
  set: (page: string | number) => {
    if (typeof page === "string") {
      emit("update:page", parseInt(page));
    } else {
      emit("update:page", page);
    }
  },
});

const buttonPages = computed(() => {
  let startPage = Math.max(1, _page.value - 2);
  const endPage = Math.min(startPage + 4, numPages.value);
  if (endPage - startPage < 5) {
    startPage = Math.max(1, endPage - 4);
  }
  const pages: number[] = [];
  for (let p = startPage; p <= endPage; p++) {
    pages.push(p);
  }
  return pages;
});

const startIndex = computed(() => (_page.value - 1) * props.numPerPage);
const endIndex = computed(() =>
  Math.min(_page.value * props.numPerPage, props.count),
);

watch(_numPerPage, sanitizePage);
watch(_page, sanitizePage);
watch(props, sanitizePage, { deep: true });

function sanitizePage() {
  if (_page.value < 1) {
    _page.value = 1;
  } else if (_page.value > numPages.value) {
    _page.value = numPages.value;
  }
}

function goToPage(p: number) {
  _page.value = p;
}

const numPerPageOptions = computed(() => [
  {
    label: t("SV.LIT_NumberOfItemsPerPage", { count: "10" }),
    value: "10",
  },
  {
    label: t("SV.LIT_NumberOfItemsPerPage", { count: "20" }),
    value: "20",
  },
  {
    label: t("SV.LIT_NumberOfItemsPerPage", { count: "50" }),
    value: "50",
  },
  {
    label: t("SV.LIT_NumberOfItemsPerPage", { count: "100" }),
    value: "100",
  },
]);
</script>

<template>
  <div
    v-if="(numPages > 1 && count > 0) || placeholder"
    class="pagination"
  >
    <div class="pagination-wrapper">
      <div
        v-if="!placeholder"
        class="pagination-start"
      >
        <button
          id="SearchResultsPaginationFirstPageButton"
          type="button"
          :disabled="page <= 1"
          :title="$t('SV.LIT_GoToFirstPage')"
          class="hidden md:inline"
          @click="goToPage(1)"
        >
          <FAIcon class="fa-light fa-chevrons-left" />
        </button>
        <button
          id="SearchResultsPaginationPreviousPageButton"
          type="button"
          :disabled="page <= 1"
          :title="$t('SV.LIT_GoToPrevioustPage')"
          @click="goToPage(page - 1)"
        >
          <FAIcon class="fa-light fa-chevron-left" />
        </button>
        <button
          v-for="p of buttonPages"
          :key="`pagination${p}`"
          type="button"
          class="search-results-pagination-go-to-page-button"
          :data-page="p"
          :class="{
            active: p === page,
            'hidden md:inline': p < page - 1 || p > page + 1,
          }"
          :title="$t('SV.LIT_GoToPage', { page: p })"
          @click="goToPage(p)"
        >
          {{ p }}
        </button>
        <button
          id="SearchResultsPaginationNextPageButton"
          type="button"
          :disabled="page >= numPages"
          :title="$t('SV.LIT_GoToNextPage')"
          @click="goToPage(page + 1)"
        >
          <FAIcon class="fa-light fa-chevron-right" />
        </button>
        <button
          id="SearchResultsPaginationLastPageButton"
          type="button"
          :disabled="page >= numPages"
          :title="$t('SV.LIT_GoToLastPage')"
          class="hidden md:inline"
          @click="goToPage(numPages)"
        >
          <FAIcon class="fa-light fa-chevrons-right" />
        </button>
      </div>
      <div
        v-else
        class="pagination-start"
      >
        <span>
          <span
            class="placeholder"
            style="width: 24px; height: 32px"
          />
        </span>
        <span>
          <span
            class="placeholder"
            style="width: 24px; height: 32px"
          />
        </span>
        <span
          v-for="i of 5"
          :key="`paginationplaceholder${i}`"
          style="width: 24px; height: 32px"
        >
          <span
            class="placeholder"
            style="width: 24px; height: 32px"
          />
        </span>
        <span>
          <span
            class="placeholder"
            style="width: 24px; height: 32px"
          />
        </span>

        <span>
          <span
            class="placeholder"
            style="width: 24px; height: 32px"
          />
        </span>
      </div>
      <div class="pagination-end">
        <div
          v-if="!placeholder"
          class="pagination-status"
        >
          {{
            $t("SV.MES_PaginationShowing", {
              start: translations.formatNumbers(startIndex + 1),
              end: translations.formatNumbers(endIndex),
              count: translations.formatNumbers(count),
            })
          }}
        </div>
        <div
          v-else
          class="pagination-status"
        >
          <span
            class="placeholder"
            style="width: 150px"
          />
        </div>
        <template v-if="!disableNumPerPageSelect">
          <FormSelect
            v-if="placeholder"
            id="SearchResultsNumPerPageSelect"
            :label="$t('SV.LIT_NumberOfRowsPerPage')"
            hide-label
            class="num-page-select"
            small
            style="width: 100px"
          />
          <FormSelect
            v-else
            id="SearchResultsNumPerPageSelect"
            :model-value="_numPerPage.toString()"
            :label="$t('SV.LIT_NumberOfRowsPerPage')"
            :options="numPerPageOptions"
            hide-label
            class="num-page-select"
            small
            @update:model-value="(e) => (_numPerPage = parseInt(e))"
          />
        </template>
      </div>
    </div>
  </div>
  <!-- <div
    v-if="numPages > 1 && count > 0"
    class="pagination-footer"
  >
    {{
      $t("SV.MES_PaginationShowing", {
        start: translations.formatNumbers(startIndex + 1),
        end: translations.formatNumbers(endIndex),
        count: translations.formatNumbers(count),
      })
    }}
  </div> -->
</template>

<style scoped lang="postcss">
.pagination {
  @apply sticky bottom-0 bg-white px-5 py-2;
}
.pagination-wrapper {
  @apply flex items-center justify-between;
}
.pagination-status {
  @apply text-sm text-gray-600 max-md:hidden;
}
.num-page-select {
  @apply inline;
}
.pagination-end {
  @apply flex items-center gap-2;
}

.pagination-start {
  @apply flex items-center gap-1;
}

button {
  @apply rounded p-1 px-2 text-gray-600 transition-all;
  &:hover {
    @apply bg-light-700 text-black;
  }
  &.active {
    @apply pointer-events-none cursor-default bg-ccli-blue text-white;
  }
}
</style>
